import { matchesArtist, matchesTitle, normalizeText } from './matching.js';
export class EngineError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
        this.name = 'EngineError';
    }
}
/** Minimum an Karten, damit ein Spiel sinnvoll ist. */
export const MIN_PLAYABLE_TRACKS = 15;
/**
 * Wie lange das Aufdecken laeuft, bevor das Ergebnis lesbar dasteht.
 *
 * Diese Werte gehoeren in den geteilten Code, obwohl nur die
 * Oberflaeche animiert. Grund: der Server berechnet daraus die Frist
 * fuer "automatisch weiter". Standen sie nur im Client, wuerde jede
 * Aenderung an der Animation dem Host stillschweigend Lesezeit
 * wegnehmen -- genau das ist beim Verlangsamen des Zaehlers passiert:
 * bei der Untergrenze von 3 s blieben statt 1,8 s nur noch 0,6 s.
 */
export const YEAR_COUNT_UP_MS = 1900;
/** Einblenden von Urteil und Folgen, nachdem die Zahl steht. */
export const VERDICT_REVEAL_MS = 600;
/**
 * Gesamtdauer vom Phasenwechsel bis zum vollstaendig lesbaren Ergebnis.
 *
 * Wird auf `autoAdvanceDelaySec` AUFGESCHLAGEN, damit die Einstellung
 * bedeutet, was sie verspricht: acht Sekunden heisst acht Sekunden
 * lesbares Ergebnis, nicht acht Sekunden abzueglich einer Animation,
 * deren Laenge der Host nicht kennen kann.
 */
export const REVEAL_ANIMATION_MS = YEAR_COUNT_UP_MS + VERDICT_REVEAL_MS;
export const defaultDeps = {
    random: Math.random,
    now: () => Date.now(),
};
// ---------------------------------------------------------------------------
// Timeline-Logik -- der eigentliche Kern von Hitster
// ---------------------------------------------------------------------------
/**
 * Prueft, ob eine Karte an `slotIndex` chronologisch korrekt liegt.
 *
 * `slotIndex` ist ein Einfuege-Index:
 *   0                -> vor der ersten Karte
 *   timeline.length   -> nach der letzten Karte
 *   i                -> zwischen timeline[i-1] und timeline[i]
 *
 * Gleiche Jahre sind auf beiden Seiten erlaubt (`<=`), weil bei
 * Jahresgleichstand jede der beiden Positionen richtig ist.
 */
export function isPlacementCorrect(timeline, slotIndex, year) {
    if (slotIndex < 0 || slotIndex > timeline.length)
        return false;
    const before = slotIndex > 0 ? timeline[slotIndex - 1] : undefined;
    const after = slotIndex < timeline.length ? timeline[slotIndex] : undefined;
    if (before !== undefined && before.year > year)
        return false;
    if (after !== undefined && after.year < year)
        return false;
    return true;
}
/** Fuegt eine Karte jahres-sortiert in eine Timeline ein (neue Kopie). */
export function insertSorted(timeline, card) {
    const next = [...timeline];
    let index = next.findIndex((existing) => existing.year > card.year);
    if (index === -1)
        index = next.length;
    next.splice(index, 0, card);
    return next;
}
/** Wandelt eine Deckkarte in eine Timeline-Karte. Setzt ein bekanntes Jahr voraus. */
export function toTimelineCard(deckCard) {
    const { track } = deckCard;
    if (track.year === null) {
        throw new Error(`Track ${track.id} hat kein aufgeloestes Jahr`);
    }
    return {
        cardId: deckCard.cardId,
        trackId: track.id,
        uri: track.uri,
        title: track.title,
        artists: track.artists,
        coverUrl: track.coverUrl,
        year: track.year,
        yearSource: track.yearSource,
    };
}
// ---------------------------------------------------------------------------
// Deck
// ---------------------------------------------------------------------------
/** Fisher-Yates, in-place auf einer Kopie. */
export function shuffle(items, random) {
    const result = [...items];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        const a = result[i];
        const b = result[j];
        result[i] = b;
        result[j] = a;
    }
    return result;
}
/**
 * Wie weit beim Ziehen nach einer bereits geprueften Karte gesucht wird.
 * Klein genug, dass die gemischte Reihenfolge erhalten bleibt.
 */
export const VERIFIED_SCAN_WINDOW = 12;
export function buildDeck(tracks, settings, random, 
/**
 * Songs aus den letzten Partien. Sie bleiben im Deck, wandern aber
 * nach unten -- so wiederholt sich ein Abend nicht staendig, und bei
 * einer kleinen Playlist geht trotzdem nichts verloren.
 */
recentTrackIds = new Set()) {
    const usable = settings.requireResolvedYear ? tracks.filter((t) => t.year !== null) : [...tracks];
    const fresh = [];
    const recent = [];
    for (const track of usable) {
        (recentTrackIds.has(track.id) ? recent : fresh).push(track);
    }
    // Beide Gruppen getrennt mischen, dann die frischen nach oben.
    // Das Deck wird von hinten gezogen, also kommen sie zuerst.
    const ordered = [...shuffle(recent, random), ...shuffle(fresh, random)];
    return ordered.map((track, index) => ({
        cardId: `c${index}_${track.id}`,
        track,
    }));
}
/**
 * Gilt das Jahr dieses Tracks als belegt?
 *
 * Nur MusicBrainz (Erstveroeffentlichung der Aufnahme) und manuelle
 * Korrekturen zaehlen. Spotifys Album-Datum ist bei Remastern und
 * Compilations unbrauchbar -- damit zu werten macht das Spiel unfair.
 */
export function isYearVerified(track) {
    if (track.year === null)
        return false;
    return track.yearSource === 'musicbrainz' || track.yearSource === 'manual';
}
/**
 * Zieht die naechste spielbare Karte.
 * Karten ohne Jahr landen im Ablagestapel. Ist das Deck leer, wird der
 * Ablagestapel neu gemischt, damit ein langer Abend nicht abbricht.
 *
 * Ist `requireVerifiedYear` aktiv, wird die oberste Karte mit bereits
 * belegtem Jahr bevorzugt. Das Deck ist gemischt, die Auswahl bleibt
 * also zufaellig -- es wird nur vermieden, dass die Runde auf eine
 * MusicBrainz-Anfrage warten muss, obwohl weiter unten geprueffte
 * Karten liegen.
 */
function drawCard(state, deps) {
    if (state.settings.requireVerifiedYear) {
        /**
         * Nur ein begrenztes Fenster durchsuchen.
         *
         * Ohne Begrenzung wird IMMER die oberste geprueffte Karte gezogen --
         * und geprueft sind vor allem Songs, die schon einmal gespielt
         * wurden, denn die stehen im Jahres-Cache. Ergebnis: dieselben
         * Lieder kommen Abend fuer Abend wieder. Das Fenster begrenzt, wie
         * weit von der gemischten Reihenfolge abgewichen wird.
         */
        const windowSize = Math.min(VERIFIED_SCAN_WINDOW, state.deck.length);
        const lowest = state.deck.length - windowSize;
        for (let index = state.deck.length - 1; index >= lowest; index--) {
            const candidate = state.deck[index];
            if (candidate && isYearVerified(candidate.track)) {
                state.deck.splice(index, 1);
                return candidate;
            }
        }
    }
    for (let attempt = 0; attempt < 2; attempt++) {
        while (state.deck.length > 0) {
            const card = state.deck.pop();
            if (card.track.year !== null)
                return card;
            state.discard.push(card);
        }
        // Deck leer -> Ablage zurueckmischen und ein zweites Mal versuchen.
        const recyclable = state.discard.filter((c) => c.track.year !== null);
        if (recyclable.length === 0)
            return null;
        state.deck = shuffle(recyclable, deps.random);
        state.discard = state.discard.filter((c) => c.track.year === null);
    }
    return null;
}
/** Wie viele Vorschlaege der Auswahl-Modus anbietet. */
const GUESS_CHOICE_COUNT = 4;
/**
 * Legt die Vorschlaege fuer den Auswahl-Modus fest.
 *
 * MUSS nach jedem Wechsel der laufenden Karte aufgerufen werden --
 * sonst zeigen die Vorschlaege noch den vorherigen Song, und der
 * richtige fehlt oder ist der falsche.
 *
 * Die Ablenker kommen aus derselben Playlist. Songs aus einer fremden
 * Quelle waeren als Ablenker wertlos: sie wirken sofort deplatziert,
 * und die richtige Antwort faellt durch Ausschluss auf.
 */
function assignGuessChoices(state, deps) {
    const card = state.currentCard;
    if (!card || !state.settings.allowTitleArtistGuess || !state.settings.guessMultipleChoice) {
        state.guessChoices = [];
        return;
    }
    const correct = { title: card.track.title, artists: card.track.artists };
    /**
     * Ablenker aus Deck und Ablage.
     *
     * Die Zeitleisten bleiben bewusst aussen vor: dort liegen bereits
     * aufgedeckte Karten, und ein Song, den alle schon gesehen haben,
     * scheidet als Ablenker sofort aus.
     */
    const pool = [...state.deck, ...state.discard]
        .map((entry) => entry.track)
        .filter((track) => track.id !== card.track.id);
    const seenTitles = new Set([normalizeText(correct.title)]);
    /**
     * Auch Interpreten nur einmal.
     *
     * Ein Ablenker desselben Interpreten wuerde als "Interpret richtig"
     * gewertet, obwohl der Spieler den falschen Song gewaehlt hat. Im
     * Auswahl-Modus ist das nicht erklaerbar.
     */
    const seenArtists = new Set(correct.artists.map((artist) => normalizeText(artist)));
    const decoys = [];
    for (const track of shuffle(pool, deps.random)) {
        if (decoys.length >= GUESS_CHOICE_COUNT - 1)
            break;
        const titleKey = normalizeText(track.title);
        if (seenTitles.has(titleKey))
            continue;
        if (track.artists.some((artist) => seenArtists.has(normalizeText(artist))))
            continue;
        seenTitles.add(titleKey);
        for (const artist of track.artists)
            seenArtists.add(normalizeText(artist));
        decoys.push({ title: track.title, artists: track.artists });
    }
    /**
     * Ohne echte Auswahl keine Auswahl.
     *
     * Bei einer sehr kleinen Playlist bleiben womoeglich keine Ablenker
     * uebrig. Ein einziger Vorschlag waere die Antwort im Klartext --
     * dann bleibt die Liste leer und die Oberflaeche zeigt Freitext.
     */
    if (decoys.length === 0) {
        state.guessChoices = [];
        return;
    }
    state.guessChoices = shuffle([correct, ...decoys], deps.random);
}
// ---------------------------------------------------------------------------
// Hilfsfunktionen
// ---------------------------------------------------------------------------
function requirePlayer(state, playerId) {
    const player = state.players.find((p) => p.id === playerId);
    if (!player)
        throw new EngineError('player_not_found');
    return player;
}
function requirePhase(state, ...phases) {
    if (!phases.includes(state.phase))
        throw new EngineError('wrong_phase');
}
function requireActive(state, playerId) {
    const active = state.players[state.activePlayerIndex];
    if (!active || active.id !== playerId)
        throw new EngineError('not_your_turn');
    return active;
}
function requireHost(state, playerId) {
    if (state.hostId !== playerId)
        throw new EngineError('not_host');
}
export function activePlayer(state) {
    return state.players[state.activePlayerIndex] ?? null;
}
/**
 * Reihenfolge "im Uhrzeigersinn" ab dem Spieler nach dem aktiven.
 * Bestimmt, wer beim Klauen Vorrang hat.
 */
export function clockwiseOrderAfterActive(state) {
    const count = state.players.length;
    const order = [];
    for (let offset = 1; offset < count; offset++) {
        const player = state.players[(state.activePlayerIndex + offset) % count];
        if (player)
            order.push(player);
    }
    return order;
}
/** Spieler, die aktuell klauen koennten (verbunden, Token vorhanden, nicht am Zug). */
export function eligibleStealers(state) {
    if (!state.settings.allowSteal)
        return [];
    return clockwiseOrderAfterActive(state).filter((p) => p.connected && p.tokens > 0);
}
function bumpVersion(state) {
    state.version += 1;
}
/**
 * Startet die Partie: Deck bauen, jedem Spieler eine offene Startkarte
 * geben, Phase auf `dealing`.
 */
export function startGame(state, input, deps = defaultDeps) {
    requireHost(state, input.playerId);
    requirePhase(state, 'lobby', 'finished');
    const connected = state.players.filter((p) => p.connected);
    if (connected.length < 1)
        throw new EngineError('not_enough_players');
    const deck = buildDeck(input.tracks, state.settings, deps.random, input.recentTrackIds);
    if (deck.length < MIN_PLAYABLE_TRACKS)
        throw new EngineError('not_enough_tracks');
    state.deck = deck;
    state.discard = [];
    state.currentCard = null;
    state.activePlacement = null;
    state.activeGuess = null;
    state.guessChoices = [];
    state.stealClaims = [];
    state.stealPasses = [];
    state.lastResult = null;
    state.winnerIds = [];
    state.phaseDeadline = null;
    for (const player of state.players) {
        player.tokens = 0;
        player.timeline = [];
    }
    // Jeder Spieler bekommt genau eine offene Startkarte.
    for (const player of state.players) {
        if (!player.connected)
            continue;
        const card = drawCard(state, deps);
        if (!card)
            throw new EngineError('not_enough_tracks');
        player.timeline = [toTimelineCard(card)];
    }
    state.activePlayerIndex = 0;
    // Erster Zug soll bei einem verbundenen Spieler starten.
    const firstConnected = state.players.findIndex((p) => p.connected);
    state.activePlayerIndex = firstConnected === -1 ? 0 : firstConnected;
    state.phase = 'dealing';
    bumpVersion(state);
}
/** Zieht eine Karte fuer den aktiven Spieler und startet die Ratephase. */
export function beginTurn(state, deps = defaultDeps) {
    requirePhase(state, 'dealing', 'reveal');
    if (state.winnerIds.length > 0) {
        state.phase = 'finished';
        bumpVersion(state);
        return;
    }
    const card = drawCard(state, deps);
    if (!card)
        throw new EngineError('deck_empty');
    state.currentCard = card;
    state.activePlacement = null;
    state.activeGuess = null;
    assignGuessChoices(state, deps);
    state.stealClaims = [];
    state.stealPasses = [];
    state.lastResult = null;
    state.phase = 'playing';
    state.phaseDeadline =
        state.settings.placementTimeLimitSec > 0
            ? deps.now() + state.settings.placementTimeLimitSec * 1000
            : null;
    bumpVersion(state);
}
/**
 * Der aktive Spieler waehlt (oder korrigiert) seine Platzierung.
 * Solange die Phase `playing` ist, darf beliebig oft geaendert werden.
 */
export function setPlacement(state, playerId, placement) {
    requirePhase(state, 'playing');
    const active = requireActive(state, playerId);
    if (placement.slotIndex < 0 || placement.slotIndex > active.timeline.length) {
        throw new EngineError('invalid_slot');
    }
    state.activePlacement = { slotIndex: placement.slotIndex };
    bumpVersion(state);
}
/** Titel/Interpret-Tipp des aktiven Spielers. Nur einmal pro Karte. */
export function submitGuess(state, playerId, guess) {
    requirePhase(state, 'playing');
    requireActive(state, playerId);
    if (!state.settings.allowTitleArtistGuess)
        throw new EngineError('guess_disabled');
    if (state.activeGuess !== null)
        throw new EngineError('guess_already_submitted');
    state.activeGuess = { title: guess.title.trim(), artist: guess.artist.trim() };
    bumpVersion(state);
}
/** Token einsetzen, um den aktuellen Song zu ueberspringen. */
export function skipTrack(state, playerId, deps = defaultDeps) {
    requirePhase(state, 'playing');
    const active = requireActive(state, playerId);
    if (!state.settings.allowSkip)
        throw new EngineError('skip_disabled');
    if (active.tokens < 1)
        throw new EngineError('no_tokens');
    active.tokens -= 1;
    if (state.currentCard)
        state.discard.push(state.currentCard);
    const card = drawCard(state, deps);
    if (!card)
        throw new EngineError('deck_empty');
    state.currentCard = card;
    state.activePlacement = null;
    state.activeGuess = null;
    assignGuessChoices(state, deps);
    state.phaseDeadline =
        state.settings.placementTimeLimitSec > 0
            ? deps.now() + state.settings.placementTimeLimitSec * 1000
            : null;
    bumpVersion(state);
}
/**
 * Tauscht die laufende Karte gegen eine neue aus -- ohne Token-Kosten.
 *
 * Wird gebraucht, wenn sich das Erscheinungsjahr nicht belegen laesst:
 * die Karte ist dann unbrauchbar, und niemand soll dafuer einen Token
 * zahlen. Fuer die Spieler sieht es aus wie ein neuer Song.
 */
export function replaceCurrentCard(state, deps = defaultDeps) {
    requirePhase(state, 'playing');
    /**
     * ERST ziehen, dann ablegen.
     *
     * Andersherum liegt die auszutauschende Karte schon im Ablagestapel,
     * wenn das Deck leer ist -- und `drawCard` mischt den Ablagestapel
     * zurueck und liefert genau dieselbe Karte wieder. Der Austausch
     * waere wirkungslos und liefe im Kreis.
     */
    const previous = state.currentCard;
    const card = drawCard(state, deps);
    if (!card) {
        // Kein Ersatz vorhanden -- die laufende Karte bleibt bestehen.
        return false;
    }
    if (previous)
        state.discard.push(previous);
    state.currentCard = card;
    state.activePlacement = null;
    state.activeGuess = null;
    assignGuessChoices(state, deps);
    state.phaseDeadline =
        state.settings.placementTimeLimitSec > 0
            ? deps.now() + state.settings.placementTimeLimitSec * 1000
            : null;
    bumpVersion(state);
    return true;
}
/**
 * Der aktive Spieler bestaetigt seine Platzierung.
 * Danach oeffnet das Klau-Fenster -- oder es wird direkt aufgedeckt,
 * wenn niemand klauen kann.
 */
export function commitPlacement(state, playerId, deps = defaultDeps) {
    requirePhase(state, 'playing');
    requireActive(state, playerId);
    if (state.activePlacement === null)
        throw new EngineError('no_placement');
    if (eligibleStealers(state).length === 0) {
        resolveRound(state, deps);
        return;
    }
    state.phase = 'stealing';
    state.stealClaims = [];
    state.stealPasses = [];
    state.phaseDeadline =
        state.settings.stealWindowSec > 0 ? deps.now() + state.settings.stealWindowSec * 1000 : null;
    bumpVersion(state);
}
/** Ein anderer Spieler setzt einen Token ein, um die Karte zu klauen. */
export function claimSteal(state, playerId, slotIndex, deps = defaultDeps) {
    requirePhase(state, 'stealing');
    const player = requirePlayer(state, playerId);
    if (!state.settings.allowSteal)
        throw new EngineError('steal_disabled');
    const active = activePlayer(state);
    if (active && active.id === playerId)
        throw new EngineError('cannot_steal_own_turn');
    if (player.tokens < 1)
        throw new EngineError('no_tokens');
    if (state.stealClaims.some((c) => c.playerId === playerId)) {
        throw new EngineError('already_claimed');
    }
    if (slotIndex < 0 || slotIndex > player.timeline.length)
        throw new EngineError('invalid_slot');
    state.stealClaims.push({ playerId, slotIndex });
    state.stealPasses = state.stealPasses.filter((id) => id !== playerId);
    maybeCloseStealWindow(state, deps);
    bumpVersion(state);
}
/** Ein Spieler verzichtet aufs Klauen. */
export function passSteal(state, playerId, deps = defaultDeps) {
    requirePhase(state, 'stealing');
    requirePlayer(state, playerId);
    if (!state.stealPasses.includes(playerId))
        state.stealPasses.push(playerId);
    state.stealClaims = state.stealClaims.filter((c) => c.playerId !== playerId);
    maybeCloseStealWindow(state, deps);
    bumpVersion(state);
}
/** Deckt auf, sobald alle klau-berechtigten Spieler entschieden haben. */
function maybeCloseStealWindow(state, deps) {
    const pending = eligibleStealers(state).filter((p) => !state.stealClaims.some((c) => c.playerId === p.id) && !state.stealPasses.includes(p.id));
    if (pending.length === 0)
        resolveRound(state, deps);
}
/** Wird vom Server aufgerufen, wenn die Klau-Frist ablaeuft. */
export function expireStealWindow(state, deps = defaultDeps) {
    if (state.phase !== 'stealing')
        return;
    resolveRound(state, deps);
}
/**
 * Deckt die Karte auf und verteilt Karten und Token.
 *
 * Reihenfolge der Regeln:
 *  1. War die Platzierung des aktiven Spielers korrekt?
 *  2. Nur EIN Klau zaehlt -- der im Uhrzeigersinn erste Anspruch.
 *     Der Token ist in jedem Fall verbraucht (Einsatz).
 *  3. Klauen gelingt nur, wenn der aktive Spieler falsch lag UND der
 *     Klauende richtig platziert hat.
 *  4. Titel+Interpret korrekt erraten gibt einen Token -- unabhaengig
 *     davon, ob die Platzierung stimmte.
 */
export function resolveRound(state, deps = defaultDeps) {
    requirePhase(state, 'playing', 'stealing');
    const card = state.currentCard;
    const active = activePlayer(state);
    const placement = state.activePlacement;
    if (!card || !active)
        throw new EngineError('wrong_phase');
    const timelineCard = toTimelineCard(card);
    const year = timelineCard.year;
    // Ohne Platzierung (Timeout) gilt der Zug als falsch.
    const effectivePlacement = placement ?? { slotIndex: -1 };
    const activeCorrect = placement !== null && isPlacementCorrect(active.timeline, placement.slotIndex, year);
    // --- Klau aufloesen: Vorrang im Uhrzeigersinn ---
    const order = clockwiseOrderAfterActive(state);
    let stealResult = null;
    for (const candidate of order) {
        const claim = state.stealClaims.find((c) => c.playerId === candidate.id);
        if (!claim)
            continue;
        if (candidate.tokens < 1)
            continue; // Token zwischenzeitlich weg
        candidate.tokens -= 1; // Einsatz ist immer faellig
        const stealCorrect = isPlacementCorrect(candidate.timeline, claim.slotIndex, year);
        const awarded = !activeCorrect && stealCorrect;
        if (awarded)
            candidate.timeline = insertSorted(candidate.timeline, timelineCard);
        stealResult = {
            playerId: candidate.id,
            slotIndex: claim.slotIndex,
            correct: stealCorrect,
            awarded,
        };
        break; // nur ein Klau pro Runde
    }
    // --- Karte verteilen ---
    let awardedTo = null;
    if (activeCorrect) {
        active.timeline = insertSorted(active.timeline, timelineCard);
        awardedTo = active.id;
    }
    else if (stealResult?.awarded) {
        awardedTo = stealResult.playerId;
    }
    else {
        state.discard.push(card);
    }
    // --- Titel/Interpret-Tipp ---
    let guessResult = null;
    if (state.activeGuess !== null) {
        const titleCorrect = matchesTitle(state.activeGuess.title, card.track.title);
        const artistCorrect = matchesArtist(state.activeGuess.artist, card.track.artists);
        const bothCorrect = titleCorrect && artistCorrect;
        const tokenAwarded = bothCorrect && active.tokens < state.settings.maxTokens;
        if (tokenAwarded)
            active.tokens += 1;
        guessResult = {
            submitted: state.activeGuess,
            titleCorrect,
            artistCorrect,
            tokenAwarded,
        };
    }
    // --- Siegbedingung ---
    state.winnerIds = state.players
        .filter((p) => p.timeline.length >= state.settings.targetCards)
        .map((p) => p.id);
    state.lastResult = {
        card: timelineCard,
        activePlayerId: active.id,
        activePlacement: effectivePlacement,
        activeCorrect,
        steal: stealResult,
        guess: guessResult,
        awardedTo,
    };
    state.phase = 'reveal';
    /**
     * Die Animation kommt OBENDRAUF, sie zaehlt nicht mit.
     *
     * Die Frist beginnt hier, das Ergebnis steht aber erst nach dem
     * Hochzaehlen lesbar da. Ohne den Aufschlag frisst die Animation die
     * eingestellte Zeit auf.
     */
    state.phaseDeadline = state.settings.autoAdvance
        ? deps.now() + REVEAL_ANIMATION_MS + state.settings.autoAdvanceDelaySec * 1000
        : null;
    bumpVersion(state);
}
/** Weiter zum naechsten Spieler -- oder Spielende. */
export function advanceTurn(state, deps = defaultDeps) {
    requirePhase(state, 'reveal');
    if (state.winnerIds.length > 0) {
        state.phase = 'finished';
        state.currentCard = null;
        state.guessChoices = [];
        state.phaseDeadline = null;
        bumpVersion(state);
        return;
    }
    const count = state.players.length;
    let nextIndex = state.activePlayerIndex;
    for (let offset = 1; offset <= count; offset++) {
        const candidate = (state.activePlayerIndex + offset) % count;
        if (state.players[candidate]?.connected) {
            nextIndex = candidate;
            break;
        }
    }
    state.activePlayerIndex = nextIndex;
    beginTurn(state, deps);
}
function toPublicPlayer(player, seat) {
    return {
        id: player.id,
        name: player.name,
        seat,
        isHost: player.isHost,
        connected: player.connected,
        isLocal: player.isLocal,
        tokens: player.tokens,
        timeline: player.timeline,
        spotifyLinked: player.spotifyLinked,
        isAudioDevice: player.audioDeviceId !== null && player.audioEnabled,
        spotifyDisplayName: player.spotifyDisplayName,
        hasPremium: player.hasPremium,
    };
}
/**
 * Erzeugt die Sicht eines Clients.
 *
 * Kritisch: In `playing` und `stealing` darf NIEMAND Titel, Interpret,
 * Cover oder Jahr der laufenden Karte sehen -- sonst ist das Spiel
 * kaputt. Nach aussen geht nur die anonyme `cardId`.
 *
 * Die Platzierung ist dagegen NICHT geheim: sie ist die Vermutung des
 * aktiven Spielers, nicht die Wahrheit. Sie verraet also nichts, was
 * das Spiel kaputtmachen wuerde -- und beim Brettspiel liegt die Karte
 * ebenfalls fuer alle sichtbar auf dem Tisch, waehrend sie geschoben
 * wird.
 */
export function redactState(state, ctx) {
    const isRevealed = state.phase === 'reveal' || state.phase === 'finished';
    const active = activePlayer(state);
    let currentCard = null;
    if (state.currentCard) {
        currentCard = isRevealed
            ? { revealed: true, card: toTimelineCard(state.currentCard) }
            : { revealed: false, cardId: state.currentCard.cardId };
    }
    return {
        code: ctx.code,
        phase: state.phase,
        hostId: state.hostId,
        youId: ctx.viewerId,
        players: state.players.map(toPublicPlayer),
        spectators: ctx.spectators,
        youAreSpectator: ctx.youAreSpectator,
        youAreAudioDevice: ctx.youAreAudioDevice,
        settings: state.settings,
        playlist: ctx.playlist,
        activePlayerId: active?.id ?? null,
        deckRemaining: state.deck.length,
        currentCard,
        /**
         * Fuer alle sichtbar, auch waehrend `playing`.
         *
         * Frueher sah sie nur der aktive Spieler selbst (und der Host fuer
         * lokale Mitspieler); oeffentlich wurde sie erst im Klau-Fenster.
         * Damit passierte das Spannendste unsichtbar: die anderen sahen
         * nur, DASS jemand ueberlegt, nie WORAUF er zusteuert.
         *
         * Bewusst in Kauf genommen: Der aktive Spieler kann seine Wahl bis
         * zum Bestaetigen noch aendern und dabei die Reaktionen der Runde
         * mitnehmen. Beim Brettspiel liegt die Karte ebenso offen, dort ist
         * das Hinlegen allerdings endgueltig.
         */
        activePlacement: state.activePlacement,
        guessSubmitted: state.activeGuess !== null,
        /**
         * Vorschlaege nur waehrend der Ratephase.
         *
         * Ab `stealing` ist die Platzierung oeffentlich und das Raten
         * vorbei -- die vier Titel weiter mitzusenden wuerde nur noch die
         * naechsten Deckkarten verraten.
         */
        guessChoices: state.phase === 'playing' ? state.guessChoices : [],
        stealClaims: state.phase === 'stealing' || isRevealed ? state.stealClaims : [],
        stealPasses: state.stealPasses,
        phaseDeadline: state.phaseDeadline,
        lastResult: isRevealed ? state.lastResult : null,
        winnerIds: state.winnerIds,
        audioDeviceCount: ctx.audioDeviceCount,
        resolvingYear: ctx.resolvingYear,
        joinUrl: ctx.joinUrl,
    };
}
//# sourceMappingURL=engine.js.map